import { useLayoutEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

/**
 * The page's scroll position across a route change (iPhone app sweep, 19 Oct 2026: Back from the
 * template editor landed on an empty Templates page, still scrolled to where the taller editor
 * page had been). The router changes the page in place and keeps the window's scroll, so:
 *
 * - a new page (a link, a redirect) opens at its top;
 * - Back and Forward keep what the browser restored, but never past the end of the page now shown:
 *   a position beyond it is pulled back to the last screenful.
 *
 * Only the path counts: a filter or step in the query string keeps the page where it is.
 */

/** Where to scroll after a navigation, or null to leave the position alone. */
export function scrollAfterNavigation(
  action: 'PUSH' | 'REPLACE' | 'POP',
  scrollY: number,
  maxScroll: number,
): number | null {
  if (action !== 'POP') return scrollY === 0 ? null : 0
  const limit = Math.max(0, maxScroll)
  return scrollY > limit ? limit : null
}

function settle(action: 'PUSH' | 'REPLACE' | 'POP'): void {
  const root = document.scrollingElement ?? document.documentElement
  const target = scrollAfterNavigation(
    action,
    window.scrollY,
    root.scrollHeight - window.innerHeight,
  )
  if (target !== null) window.scrollTo({ top: target, left: 0, behavior: 'auto' })
}

/** Applies the rule above on every path change. */
export function useRouteScroll(): void {
  const { pathname, hash } = useLocation()
  const action = useNavigationType()
  useLayoutEffect(() => {
    // A link to a part of the page (#section) scrolls there itself.
    if (hash !== '') return
    settle(action)
    // The page drawn after a lazy load or a data read can be shorter than the first frame; Back
    // checks again once it has settled.
    if (action !== 'POP') return
    let second = 0
    const first = window.requestAnimationFrame(() => {
      second = window.requestAnimationFrame(() => settle(action))
    })
    const late = window.setTimeout(() => settle(action), 400)
    return () => {
      window.cancelAnimationFrame(first)
      window.cancelAnimationFrame(second)
      window.clearTimeout(late)
    }
    // Only a path change is a new page; the navigation type is read with it.
  }, [pathname])
}
